import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener, useDebounceFn } from 'ahooks';
import { Switch, Tooltip } from '@mantine/core';
import { Checkbox } from 'antd';
import { config } from '@shakkerai/config';
import { translate } from '@shakkerai/util-i18n';
import { useTempState, setTempState } from '@starflow/app/store';
import { presetNegativePromptOptions } from '@shakkerai/pro-constants';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { MODEL_SD15, MODEL_SDXL } from '@shakkerai/pro-models';
import { useGuidanceHooks } from '@shakkerai/pro-guidance';
import { Icon } from '@shakkerai/components';
import { mc } from '@shakkerai/report';

const isMainland = config.env === 'aliyun';

const PromptSettingMap = isMainland
  ? [
      {
        title: translate('prompt_setting_autofill'),
        value: 'auto_complete',
      },
      {
        title: translate('two_language'),
        value: 'two_language',
      },
      {
        title: translate('prompt_setting_enhance'),
        value: 'prompt_setting_enhance',
      },
    ]
  : [
      {
        title: translate('prompt_setting_autofill'),
        value: 'auto_complete',
      },
      {
        title: translate('prompt_setting_enhance'),
        value: 'prompt_setting_enhance',
      },
    ];

const setingReportMap = {
  prompt_setting_autofill: 'completion',
  two_language: 'translation',
  prompt_setting_enhance: 'optimization',
};

const NegativePromptOption = React.memo(({ disabled, baseType, generator }) => {
  const presetNegativePrompts = generator?.data?.presetNegativePrompts || [];
  const disabledPreset = useMemo(() => {
    return baseType !== MODEL_SD15 || disabled;
  }, [baseType, disabled]);

  const handleToggle = useCallback(
    (type) => {
      if (disabledPreset) {
        return;
      }
      const nextPresetNegativePrompts = presetNegativePrompts?.includes(type)
        ? presetNegativePrompts?.filter((item) => item !== type)
        : [...presetNegativePrompts, type];

      updateUnitData(generator.id, { presetNegativePrompts: nextPresetNegativePrompts });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator, baseType, disabledPreset, presetNegativePrompts],
  );

  return (
    <Tooltip withArrow position="top" label={translate('set_neg_prompt_disabled_tip')}>
      <div className="flex flex-wrap mt-1">
        {presetNegativePromptOptions?.map((option) => {
          const checked = !disabledPreset && presetNegativePrompts?.includes(option.value);

          return (
            <Checkbox
              key={option.value}
              size="xs"
              className={`flex-shrink-0 mb-1.5 scale-90 origin-left mr-4 ${checked ? 'ant-checkbox-checked' : ''}`}
              disabled={disabledPreset}
              checked={checked}
              onChange={() => handleToggle(option.value)}
            >
              {option.label}
            </Checkbox>
          );
        })}
      </div>
    </Tooltip>
  );
});

export const ExpandedPromptFormSetting = React.memo((props) => {
  const mode = useTempState('globalMode');
  const isSimple = mode === 'simple';
  const { disabled, generator } = props;
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const { negativePrompt } = generator.data;
  const [inputNegativePrompt, setInputNegativePrompt] = useState(negativePrompt);
  const updateUnitDataDebounced = useDebounceFn(updateUnitData, { wait: 200 });
  const promptSettingsParams = useTempState('promptSettingsParams') || {};
  const isPrimaryGenerator = generator.type === 'generator';

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const handlePrompt = useCallback(
    (inputPrompt) => {
      setInputNegativePrompt(inputPrompt);
      updateUnitDataDebounced.run(generator.id, { negativePrompt: inputPrompt });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const handleInputNegativePrompt = useCallback(
    (event) => {
      const inputPrompt = event.target.value;
      handlePrompt(inputPrompt);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handlePrompt],
  );

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="prompt-setting-subwindow"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const ref = document.getElementById(`prompt-setting-${generator.id}`);
    const { x, y } = ref.getBoundingClientRect();

    setPosition({
      x: isSimple && isPrimaryGenerator ? x - 220 : x - 268,
      y: isSimple && isPrimaryGenerator ? y - 330 : y - 330,
    });
  }, [generator.id, isPrimaryGenerator, isSimple]);

  useEffect(() => {
    setInputNegativePrompt(negativePrompt);
  }, [negativePrompt]);

  const handleSwitchChange = (event, name) => {
    setingReportMap[name] &&
      mc(`tool.prompt.settings`, {
        [setingReportMap[name]]: !!event.currentTarget.checked,
      });

    setTempState({
      promptSettingsParams: {
        ...promptSettingsParams,
        [name]: event.currentTarget.checked,
      },
    });
  };

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd default={position} scale={1} className="z-[100]" dragHandleClassName="sub-drag-handle">
      <div
        ref={containerRef}
        className="absolute z-20 w-[280px] prompt-tip-setting-popup bg-white shadow-light rounded-md"
      >
        <div className="flex items-center justify-between mb-1 prompt-tip-setting-popup-header">
          <span className="text-caption-600 font-medium sub-drag-handle cursor-move">
            {translate('prompt_setting_title')}
          </span>
          <div>
            <button onClick={handleCloseWindow}>
              <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
            </button>
          </div>
        </div>
        <div className="prompt-tip-setting-popup-main">
          {PromptSettingMap.map((set) => {
            return (
              <div className=" prompt-tip-setting-popup-item flex items-center justify-between" key={set.value}>
                <span>{set.title}</span>
                <Switch
                  checked={promptSettingsParams[set.value]}
                  size="xs"
                  onChange={(event) => handleSwitchChange(event, set.value)}
                />
              </div>
            );
          })}
          {isSimple && (
            <>
              <span className="prompt-tip-setting-popup-item">{translate('prompt_neg_title')}</span>
              <textarea
                disabled={disabled}
                value={inputNegativePrompt}
                onChange={handleInputNegativePrompt}
                placeholder={translate('prompt_negative_placeholder')}
                className="w-full h-[94px] bg-[#F4F5F7] border border-black/5 rounded transition p-[7px] pb-0 select-auto
          duration-300 resize-none text-[#474747] placeholder:text-[#999999] focus:border-black/20 text-xs caret-primary scrollbar-hidden"
              />
            </>
          )}
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});

const isArrayEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const ExpandedPromptForm = React.memo((props) => {
  const { disabled, generator } = props;
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const isSimple = useTempState('globalMode') === 'simple';
  const { presetNegativePrompts, negativePrompt } = generator.data;
  const [inputNegativePrompt, setInputNegativePrompt] = useState(negativePrompt);
  const presetNegativePromptCacheRef = useRef('');

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  useEffect(() => {
    let negativePromptTotal = negativePrompt;
    if (!isArrayEqual(presetNegativePrompts, presetNegativePromptCacheRef.current)) {
      presetNegativePromptCacheRef.current = presetNegativePrompts;
      presetNegativePromptOptions.forEach((item) => {
        if (presetNegativePrompts.includes(item.value)) {
          if (negativePromptTotal?.indexOf(item.text) === -1) {
            negativePromptTotal = `${item.text}, ${negativePromptTotal}`;
          }
        } else {
          if (negativePromptTotal?.indexOf(item.text) > -1) {
            negativePromptTotal = negativePromptTotal.replace(`${item.text}, `, '');
          }
        }
      });
      if (negativePromptTotal != negativePrompt) {
        updateUnitData(generator.id, { negativePrompt: negativePromptTotal });
      }
    }
  }, [presetNegativePrompts, generator.id, negativePrompt]);

  const updateUnitDataDebounced = useDebounceFn(updateUnitData, { wait: 200 });

  const baseType = generator.data.baseType || MODEL_SDXL;

  const handlePrompt = useCallback(
    (inputPrompt) => {
      setInputNegativePrompt(inputPrompt);
      updateUnitDataDebounced.run(generator.id, { negativePrompt: inputPrompt });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const handleInputNegativePrompt = useCallback(
    (event) => {
      const inputPrompt = event.target.value;
      handlePrompt(inputPrompt);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handlePrompt],
  );

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="prompt-subwindow"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const ref = document.getElementById('generator-prompt-container');
    const { x, width } = ref.getBoundingClientRect();

    setPosition({
      x: x + width - 300,
      y: window.innerHeight - (isSimple ? 375 : 505),
    });
  }, [isSimple]);

  useEffect(() => {
    setInputNegativePrompt(negativePrompt);
  }, [negativePrompt]);

  const { visible: guidance710Visible } = useGuidanceHooks(['RemixNow']);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd
      default={position}
      scale={1}
      className={guidance710Visible ? 'z-[71]' : 'z-[51]'}
      dragHandleClassName="sub-drag-handle"
    >
      <div ref={containerRef} className="z-20 w-[300px] bg-white shadow-light rounded-sm">
        <div className="sub-drag-handle flex items-center justify-between px-[16px] py-[12px] cursor-move border-b border-b-[#EFEFEF]">
          <span className="text-[#2d2d2d] font-bold font-grotesk text-sm">{translate('prompt_neg_setting_title')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
          </button>
        </div>
        <div className="px-[16px] py-[16px]">
          <div>
            <textarea
              disabled={disabled}
              value={inputNegativePrompt}
              onChange={handleInputNegativePrompt}
              placeholder={translate('prompt_negative_placeholder')}
              className="w-full h-[100px] bg-[#F4F5F7] border border-black/5 rounded transition p-[7px] pb-0 select-auto
          duration-300 resize-none text-[#474747] placeholder:text-[#999999] focus:border-black/20 text-xs caret-primary scrollbar-hidden"
            />
          </div>
          {!isSimple && (
            <>
              <div className="text-xs text-caption-600 font-semibold mt-[10px]">{translate('prompt_neg_prest')}</div>
              <div className="mt-[16px]">
                <NegativePromptOption disabled={disabled} baseType={baseType} generator={generator} />
              </div>
            </>
          )}
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});
